import { TweenMax, gsap } from 'gsap/all';
import ScrollToPlugin     from 'gsap/ScrollToPlugin';
gsap.registerPlugin( ScrollToPlugin );

import create             from 'dom-create-element';

import getClosest         from '../utils/dom/getClosest';
import regex_email        from '../utils/regex/email';

const defaultOptions = {
  selectorGroup: '.form-group',
  classErrorElement: 'form-error',
  classIsError: 'is-error',
  animateToFirstError: true,
  i18n: {
    email: Translator ? Translator.trans('form_errors.email') : 'Merci de renseigner un email valide.',
    same: Translator ? Translator.trans('form_errors.same') : 'Les champs ne correspondent pas.',
    empty: Translator ? Translator.trans('form_errors.empty') : 'Ce champs est obligatoire.',
    checkbox: Translator ? Translator.trans('form_errors.checkbox') : 'Veuillez accepter les conditions.',
    radio: Translator ? Translator.trans('form_errors.radio') : 'Veuillez sélectionner au moins un choix.'
  }
};

class FormControl {
  //________________________________________________________ constructor
  // -
  constructor(el, options = {}) {
    this._el = el;
    this._dom = {};
    this._options = {...defaultOptions, ...options};

    this._formCreateBound();
    this._el.setAttribute('novalidate', 'novalidate');

    this._formGrabDom();
    this._formAddEvents();
  }

  set options(options) {
    this._options = {
      ...this._options,
      options
    };
  }

  get options() {
    return this._options;
  }

  get isValidate() {
    return this._formValidate();
  }

  //________________________________________________________ public
  // -
  destroy() {
    this._el.removeEventListener('submit', this._onFormSubmit);
    this._domForm.inputs.forEach( input => {
      input.removeEventListener('keyup', this._onFormKeyUp);
      if (input.getAttribute('type') === 'checkbox' ||
        input.getAttribute('type') === 'radio' ||
        input.tagName === 'SELECT') {
        input.removeEventListener('change', this._onFormKeyUp);
      }
    })
  }

  removeErrors() {
    const isErrors = [...this._el.querySelectorAll(`${this._options.classIsError}`)];
    const currentErrors = [...this._el.querySelectorAll(`.${this._options.classErrorElement}`)];
    currentErrors.forEach(error => {
      error.parentNode.removeChild(error);
    });
    isErrors.forEach(error => {
      error.classList.remove(this._options.classIsError);
    });
  }

  setError(element, message = null) {
    const parent = getClosest(element, this._options.selectorGroup);
    if( parent) {
      let error = parent.querySelector(`.${this._options.classErrorElement}`);
      if(!error)  {
        error = create({
          selector: 'ul',
          styles: this._options.classErrorElement
        });
        parent.appendChild(error);
      }
      parent.classList.add(this._options.classIsError);
      if(message) {
        const errorElement = document.createElement('li');
        errorElement.innerHTML = message;
        error.appendChild(errorElement);
      }
    }
  }

  //________________________________________________________ events
  // -
  _onFormSubmit(pEvt) {
    if (!this._formValidate()) {
      pEvt.preventDefault();
    }
  }

  _onFormKeyUp(pEvt) {
    var el = pEvt.target,
      parent = getClosest(el, this._options.selectorGroup);
    parent.classList.remove(this._options.classIsError);
    const currentErrors = [...parent.querySelectorAll(`.${this._options.classErrorElement}`)];
    currentErrors.forEach(error => {
      error.classList.remove(this._options.classIsError);
      error.parentNode.removeChild(error);
    });
  }

  //________________________________________________________ private
  // -
  _formCreateBound() {
    ['_onFormKeyUp', '_onFormSubmit']
      .forEach((fn) => this[fn] = this[fn].bind(this));
  }

  _formGrabDom() {
    this._domForm = {};
    let texts = [...this._el.querySelectorAll('input[type=text]')],
      hiddens = [...this._el.querySelectorAll('input[type=hidden]')],
      emails = [...this._el.querySelectorAll('input[type=email]')],
      tels = [...this._el.querySelectorAll('input[type=tel]')],
      passwords = [...this._el.querySelectorAll('input[type=password]')],
      textarea = [...this._el.querySelectorAll('textarea')],
      radios = [...this._el.querySelectorAll('input[type=radio]')],
      checkbox = [...this._el.querySelectorAll('input[type=checkbox]')],
      numbers = [...this._el.querySelectorAll('input[type=number]')],
      selects = [...this._el.querySelectorAll('select')];
    this._domForm.inputs = [...texts, ...hiddens, ...emails, ...passwords, ...tels, ...numbers, ...checkbox, ...radios, ...selects, ...textarea];
  }

  _formValidate() {
    let validate = true,
      errorEl = null;
    this.removeErrors();
    for (let i = 0; i < this._domForm.inputs.length; i++) {
      let el = this._domForm.inputs[i],
        parent = getClosest(el, this._options.selectorGroup);
      if (parent === null) continue;
      if (el.getAttribute('disabled')) continue;
      if (el.getAttribute('type') && el.getAttribute('type') === 'email' && (!regex_email(el.value) && el.value !== '')) {
        validate = false;
        this.setError(el, this._options.i18n.email);
      }
      if (el.getAttribute('data-formvalidate-same') && this._el.querySelector(`[data-formvalidate-same=${el.getAttribute('data-formvalidate-same')}]`).value !== el.value) {
        validate = false;
        this.setError(el, this._options.i18n.same);
      }
      if (el.getAttribute('required') !== null) {
        if (el.getAttribute('type') === 'radio') {
          let isChecked = false;
          let els = [...this._el.querySelectorAll(`input[type="radio"][name="${el.getAttribute('name')}"]`)];
          for (let i = 0; i < els.length; i++) {
            if (els[i].checked) {
              isChecked = true;
              break;
            }
          }
          if (!isChecked) {
            validate = false;
            this.setError(el, this._options.i18n.radio);
          }
        }
        if (el.getAttribute('type') === 'checkbox' && !el.checked) {
          validate = false;
          this.setError(el, this._options.i18n.checkbox);
        } else if (el.value === '') {
          validate = false;
          const message = el.getAttribute('type') !== 'hidden' ? this._options.i18n.empty : null;
          this.setError(el, message);
        }
      }
      if (!validate && errorEl === null) errorEl = el;
    }
    if (!validate && errorEl && this._options.animateToFirstError) {
      TweenMax.to(window, .6, { scrollTo: (window.pageYOffset + errorEl.getBoundingClientRect().top - 150) })
    }
    return validate;
  }

  _formAddEvents() {
    this._el.addEventListener('submit', this._onFormSubmit);
    this._domForm.inputs.forEach( input => {
      input.addEventListener('keyup', this._onFormKeyUp);
      if (input.getAttribute('type') === 'checkbox' ||
        input.getAttribute('type') === 'radio' ||
        input.tagName === 'SELECT') {
        input.addEventListener('change', this._onFormKeyUp);
      }
    });
  }
}

export default FormControl;
